import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Leaf, Zap, Users } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About Us</h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-2xl">
            Connecting artbuyers and arsellers with quality artworks, products and exceptional service since day one.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-600 mb-4">
                At AllArtWeb, we believe in making shopping seamless and rewarding. Our mission is to connect quality sellers with customers who appreciate value and authentic products.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We're committed to building a trusted marketplace where every transaction is secure, every product is genuine, and every customer feels valued.
              </p>
              <Link
                to="/products"
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Shop Now
              </Link>
            </div>
            <div className="bg-blue-50 rounded-lg p-8">
              <div className="aspect-square bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center">
                <Heart className="w-24 h-24 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Trust */}
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-blue-100 p-4 rounded-full">
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Trust</h3>
              <p className="text-gray-600">
                We build relationships based on transparency and honesty in every interaction.
              </p>
            </div>

            {/* Quality */}
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-green-100 p-4 rounded-full">
                  <Leaf className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quality</h3>
              <p className="text-gray-600">
                We ensure every product meets our high standards before reaching you.
              </p>
            </div>

            {/* Innovation */}
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-yellow-100 p-4 rounded-full">
                  <Zap className="w-8 h-8 text-yellow-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Innovation</h3>
              <p className="text-gray-600">
                We continuously improve our platform to serve you better.
              </p>
            </div>

            {/* Community */}
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-pink-100 p-4 rounded-full">
                  <Heart className="w-8 h-8 text-pink-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Community</h3>
              <p className="text-gray-600">
                We support sellers and customers in building a vibrant marketplace.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">8k+</div>
              <p className="text-xl text-blue-100">Products Listed</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">30K+</div>
              <p className="text-xl text-blue-100">Happy Customers</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">500+</div>
              <p className="text-xl text-blue-100">Trusted Sellers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team/Story Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Story</h2>
          <div className="bg-gray-50 rounded-lg p-8 md:p-12">
            <p className="text-lg text-gray-600 mb-6">
              AllArtWeb was founded with a simple vision: to create a marketplace where quality sellers can reach customers who appreciate authenticity and value, buyers to easily find authentic art products and services for vibrants homes, offices and environments that brigten the mood of their choosing. We started small, focusing on understanding what our customers and sellers really need.
            </p>
            <p className="text-lg text-gray-600 mb-6">
              Today, we're proud to be one of the most trusted online marketplaces in the region. Our success is built on the trust of our community and our commitment to continuous improvement.
            </p>
            <p className="text-lg text-gray-600">
              We believe the best is yet to come. Join us in shaping the future of online shopping.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-50 py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Have Questions?</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            We're here to help. Reach out to us anytime with questions, feedback, or suggestions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Contact Us
            </Link>
            <Link
              to="/faq"
              className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold border-2 border-blue-600 hover:bg-blue-50 transition-colors"
            >
              FAQ
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
